import { datadogRum } from '@datadog/browser-rum';

import { ANALYTICS_EVENTS } from '../../events';
import { AnalyticsDestination } from '../../types';
import { normalizeEmail } from '../../utils';

import { cioSerializeCheckout, cioSerializeCartItem } from './serializers';
import { CIOSubscription } from './types';

declare global {
  interface Window {
    cioanalytics?: {
      page: (...args: any[]) => void;
      track: (...args: any[]) => void;
      identify: (...args: any[]) => void;
    };
  }
}

// The Customer.io analytics client is injected into the global `window` object
// by GTM or the tracking snippet included via `customerio_script()`.
// A bare array here is sendHeldCioEvents' pre-consent queue (docs/analytics.md).
export const getCio = () => {
  if (typeof window !== 'undefined' && typeof window.cioanalytics?.track === 'function') {
    return window.cioanalytics;
  }
  return undefined;
};

export const CIO_HELD_EVENTS_ELEMENT_ID = 'cio_held_events';

type HeldCioEvent = { event: string; properties: Record<string, any> };

// Bypasses cioDestination.track so each event keeps the timestamp of the add it records.
export const sendHeldCioEvents = () => {
  const element = document.getElementById(CIO_HELD_EVENTS_ELEMENT_ID);
  if (!element) {
    return;
  }
  element.remove();

  let held: HeldCioEvent[];
  try {
    held = JSON.parse(element.textContent || '[]');
  } catch (error) {
    datadogRum.addError(error, {
      module: 'Analytics',
      destination: 'customerio',
      method: 'sendHeldCioEvents',
    });
    return;
  }

  const cio = getCio();
  if (cio) {
    held.forEach(({ event, properties }) => cio.track(event, properties));
    return;
  }
  // Before consent: Customer.io's snippet adopts this array and sends it once it loads.
  const w = window as unknown as { cioanalytics?: unknown[] };
  w.cioanalytics = w.cioanalytics || [];
  held.forEach(({ event, properties }) => w.cioanalytics!.push(['track', event, properties]));
};

// Store region ids are locale strings like "en-us"; CIO abandoned-cart
// campaigns want just the country portion ("us", "gb", ...).
const toCioRegion = (region?: Region): string | undefined => region?.replace(/^en-/, '');

export const cioDestination: AnalyticsDestination = {
  identify(id: string, traits: Record<string, any> = {}) {
    getCio()?.identify(id, traits);
  },

  identifyByEmail(email: string, traits: Record<string, any> = {}) {
    const normalizedEmail = normalizeEmail(email);
    this.identify(normalizedEmail, { ...traits, email });
  },

  track(event: string, data: Record<string, any> = {}) {
    data.timestamp = Math.floor(Date.now() / 1000);
    getCio()?.track(event, data);
  },

  trackCheckoutStarted(
    cart: Cart,
    subscription: CIOSubscription,
    utmData?: UtmData,
    region?: Region,
  ) {
    let payload: Record<string, any>;
    try {
      payload = cioSerializeCheckout(cart, subscription);
      if (utmData) {
        payload = { ...payload, ...utmData };
      }
    } catch (error) {
      payload = { cart_id: cart?.id, serialization_failed: true };
      datadogRum.addError(error, {
        module: 'Analytics',
        destination: 'customerio',
        method: 'trackCheckoutStarted',
        ...payload,
      });
    }

    const regionCode = toCioRegion(region);
    if (regionCode) {
      payload.region = regionCode;
    }

    this.track(ANALYTICS_EVENTS.CHECKOUT_STARTED, payload);
  },

  trackRemoveFromCart(cart: Cart, removedKit: Kit, utmData?: UtmData, region?: Region) {
    let payload: Record<string, any>;
    try {
      payload = cioSerializeCartItem(removedKit, cart);
      if (utmData) {
        payload = { ...payload, ...utmData };
      }
    } catch (error) {
      payload = { cart_id: cart?.id, kit_id: removedKit?.kitId, serialization_failed: true };
      datadogRum.addError(error, {
        module: 'Analytics',
        destination: 'customerio',
        method: 'trackRemoveFromCart',
        ...payload,
      });
    }

    const regionCode = toCioRegion(region);
    if (regionCode) {
      payload.region = regionCode;
    }

    this.track(ANALYTICS_EVENTS.REMOVE_FROM_CART, payload);
  },

  page(nameOrProps?: string | Record<string, any>, props?: Record<string, any>) {
    getCio()?.page(nameOrProps, props);
  },
};
